import React, { useMemo } from 'react';
import Input from '../../shared/Input';
import CustomSelect from '../../shared/CustomSelect';
import { Compass, XCircle } from 'lucide-react';
import { countryRequiresPostalCode } from '../../../utils/countryPostal';
import {
 countryHasStateList,
 getStateOptionsForCountry,
 isValidStateForCountry,
} from '../../../utils/countryStates';

const Step2ContactLocation = ({
 formData,
 errors,
 markets = [],
 marketOptions = [],
 marketsLoading = false,
 marketsError = null,
 socialPlatforms = [],
 socialPlatformsLoading = false,
 selectedOptionalPlatforms = [],
 onToggleOptionalPlatform,
 geoHint = {},
 onSuggestFromGeo,
 handleInputChange,
 handleSocialMediaChange,
 removeSocialMediaPlatform,
}) => {
 const selectedMarket = useMemo(() => {
 if (!formData.country) return null;
 return (markets || []).find(
 (m) => String(m.country_code).toUpperCase() === String(formData.country).toUpperCase()
 );
 }, [markets, formData.country]);

 const marketNote = useMemo(() => {
 if (marketsLoading) {
 return 'Loading operating markets…';
 }
 if (marketsError) {
 return 'Could not load markets. Refresh and try again.';
 }
 if (!marketOptions.length) {
 return 'No markets are set up yet. An admin must add markets before you can choose where you operate.';
 }
 if (!formData.country) {
 return 'Choose the market where you operate. Bookings, tax, and payouts follow that market.';
 }
 if (selectedMarket?.status === 'active' && selectedMarket.bookings_enabled) {
 return `“${selectedMarket.display_name}” is open for bookings.`;
 }
 if (selectedMarket?.status === 'draft') {
 return `“${selectedMarket.display_name}” is still a draft. You can verify now. Clients can book after an admin activates it.`;
 }
 if (selectedMarket?.status === 'paused') {
 return `“${selectedMarket.display_name}” is paused. Bookings are temporarily unavailable.`;
 }
 if (selectedMarket) {
 return `“${selectedMarket.display_name}” status: ${selectedMarket.status}.`;
 }
 return 'Choose a market from the list.';
 }, [formData.country, marketsError, marketsLoading, marketOptions.length, selectedMarket]);

 const postalRequired = countryRequiresPostalCode(formData.country);
 const stateOptions = useMemo(
 () => getStateOptionsForCountry(formData.country),
 [formData.country]
 );
 const hasStateList = countryHasStateList(formData.country);

 const handleCountryChange = (value) => {
 handleInputChange('country', value);
 // Clear state when it doesn't belong to the newly selected country
 if (formData.state && !isValidStateForCountry(value, formData.state)) {
 handleInputChange('state', '');
 }
 };

 const activePlatforms = useMemo(
 () => (socialPlatforms || []).filter((platform) => platform.is_active !== false),
 [socialPlatforms]
 );

 const requiredPlatforms = activePlatforms.filter(
 (platform) => platform.is_required || platform.key === 'instagram'
 );
 const optionalPlatforms = activePlatforms.filter(
 (platform) => !(platform.is_required || platform.key === 'instagram')
 );

 const visibleOptional = optionalPlatforms.filter((platform) =>
 selectedOptionalPlatforms.includes(platform.key)
 );

 return (
 <div className="space-y-4 sm:space-y-6">
 <div>
 <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2 sm:mb-4">Contact & Location</h3>
 <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
 Contact details and the market where you operate. The list is the markets an admin has set up.
 </p>
 </div>

 <div className="rounded-xl border border-border bg-surface-muted/80 px-3 py-3 text-xs sm:text-sm text-ink space-y-2">
 <p>{marketNote}</p>
 {onSuggestFromGeo && (
 <button
 type="button"
 onClick={onSuggestFromGeo}
 disabled={geoHint.loading}
 className="inline-flex items-center gap-1.5 text-ink font-medium hover:underline disabled:opacity-60"
 >
 <Compass className="w-3.5 h-3.5" />
 {geoHint.loading ? 'Detecting…' : 'Suggest from current connection (optional)'}
 </button>
 )}
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
 <Input
 label="Business Phone *"
 type="tel"
 value={formData.business_phone}
 onChange={(e) => {
 const value = e.target.value;
 if (value === '' || /^[\d\s()\-+]+$/.test(value)) {
 handleInputChange('business_phone', value);
 }
 }}
 onKeyDown={(e) => {
 if (!/[\d\s()\-+]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
 e.preventDefault();
 }
 }}
 onPaste={(e) => {
 const paste = e.clipboardData.getData('text');
 if (!/^[\d\s()\-+]+$/.test(paste)) {
 e.preventDefault();
 }
 }}
 error={errors.business_phone}
 placeholder="(555) 123-4567"
 className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
 />

 <Input
 label="Business Email *"
 type="email"
 value={formData.business_email}
 onChange={(e) => handleInputChange('business_email', e.target.value)}
 error={errors.business_email}
 placeholder="business@example.com"
 />
 </div>

 <Input
 label="Website"
 value={formData.website}
 onChange={(e) => handleInputChange('website', e.target.value)}
 placeholder="https://yourwebsite.com"
 />

 <div>
 <h4 className="text-sm sm:text-md font-medium text-gray-900 mb-1">Social Media</h4>
 <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
 Instagram is required. Select any other platforms you use and enter your handle.
 </p>

 {socialPlatformsLoading ? (
 <p className="text-xs sm:text-sm text-gray-500">Loading social platforms…</p>
 ) : (
 <div className="space-y-4">
 {requiredPlatforms.map((platform) => (
 <Input
 key={platform.key}
 label={`${platform.label} *`}
 value={formData.social_media?.[platform.key] || ''}
 onChange={(e) => handleSocialMediaChange(platform.key, e.target.value)}
 placeholder={platform.placeholder || '@yourhandle'}
 error={errors[`social_${platform.key}`] || (platform.key === 'instagram' ? errors.instagram : null)}
 />
 ))}

 {optionalPlatforms.length > 0 && (
 <div>
 <p className="text-xs sm:text-sm font-medium text-gray-700 mb-2">Add more platforms</p>
 <div className="flex flex-wrap gap-2">
 {optionalPlatforms.map((platform) => {
 const selected = selectedOptionalPlatforms.includes(platform.key);
 return (
 <button
 key={platform.key}
 type="button"
 onClick={() => onToggleOptionalPlatform?.(platform.key)}
 className={`rounded-full border px-3 py-1.5 text-xs sm:text-sm transition-colors ${ selected ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 bg-surface text-gray-700 hover:border-gray-500' }`}
 >
 {selected ? '✓ ' : '+ '}
 {platform.label}
 </button>
 );
 })}
 </div>
 </div>
 )}

 {visibleOptional.map((platform) => (
 <div key={platform.key} className="flex flex-col sm:flex-row items-start sm:items-end gap-2 sm:gap-3">
 <div className="flex-1 w-full">
 <Input
 label={platform.label}
 value={formData.social_media?.[platform.key] || ''}
 onChange={(e) => handleSocialMediaChange(platform.key, e.target.value)}
 placeholder={platform.placeholder || '@yourhandle'}
 error={errors[`social_${platform.key}`]}
 />
 </div>
 <button
 type="button"
 onClick={() => {
 onToggleOptionalPlatform?.(platform.key);
 removeSocialMediaPlatform?.(platform.key);
 }}
 className="text-red-600 hover:text-red-700 p-2 self-start sm:self-center"
 title="Remove platform"
 >
 <XCircle className="w-5 h-5" />
 </button>
 </div>
 ))}
 </div>
 )}
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
 <CustomSelect
 label="Operating market *"
 value={formData.country || ''}
 onChange={handleCountryChange}
 options={marketOptions}
 placeholder={marketsLoading ? 'Loading markets…' : 'Select market'}
 error={errors.country || (marketsError ? 'Failed to load markets' : '')}
 disabled={marketsLoading || marketOptions.length === 0}
 size="md"
 />
 </div>

 <div>
 <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
 Business Address *
 </label>
 <textarea
 value={formData.address}
 onChange={(e) => handleInputChange('address', e.target.value)}
 rows={2}
 className={`w-full px-3 py-1.5 text-xs sm:text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-ink ${ errors.address ? 'border-red-300' : 'border-gray-300' }`}
 placeholder="Street address, suite, etc."
 />
 {errors.address && (
 <p className="mt-1 text-xs sm:text-sm text-red-600">{errors.address}</p>
 )}
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
 <Input
 label="City *"
 value={formData.city}
 onChange={(e) => handleInputChange('city', e.target.value)}
 error={errors.city}
 placeholder="City"
 />

 {hasStateList ? (
 <CustomSelect
 label="State / Province *"
 value={formData.state || ''}
 onChange={(value) => handleInputChange('state', value)}
 options={stateOptions}
 placeholder={formData.country ? 'Select state / province' : 'Select market first'}
 error={errors.state}
 disabled={!formData.country}
 size="md"
 />
 ) : (
 <Input
 label="State / Province *"
 value={formData.state}
 onChange={(e) => handleInputChange('state', e.target.value)}
 error={errors.state}
 placeholder={formData.country ? 'State / Province' : 'Select market first'}
 disabled={!formData.country}
 />
 )}

 <Input
 label={postalRequired ? 'ZIP / Postal *' : 'ZIP / Postal'}
 value={formData.zip_code}
 onChange={(e) => handleInputChange('zip_code', e.target.value)}
 error={errors.zip_code}
 placeholder={postalRequired ? '12345' : 'Optional'}
 />
 </div>
 </div>
 );
};

export default Step2ContactLocation;
